/**
 * Referrer commission display model (Master Enhancement Plan §2.1d / §2.4).
 *
 * Consumes the normalized records returned by `GET /api/referrer/commissions`
 * (see AGENTS.md "Commission tracking": the server merge normalizes legacy
 * docs and exposes display-friendly `status`/`eligibility` plus pipeline
 * context). Every referrer surface — dashboard card, home payouts card,
 * My Commissions, Commission Tracking — buckets and totals through THIS
 * module so the numbers always match each other.
 *
 * Amounts: the amount a referrer earns for a candidate is the rate STAMPED
 * on the commission record when the referral was made (PR-0b commission
 * integrity). The client never recomputes an amount from the referrer's
 * current program rates — it reads the record's amount (preferring an
 * explicit stamped amount field when the API provides one).
 */

export type CommissionBucket = 'accruing' | 'payable' | 'paid' | 'cancelled';

/** The stamped amount for a commission record (never a recomputed rate). */
export function commissionAmount(c: any): number {
  const v = Number(c?.stampedAmount ?? c?.amount ?? c?.rate ?? 0);
  return Number.isFinite(v) ? v : 0;
}

const PAID_STATUSES = new Set(['paid']);
const CANCELLED_STATUSES = new Set(['cancelled', 'ineligible', 'rejected', 'removed']);

/**
 * Bucket a commission record:
 * - paid:      payout actually processed (money moved / recorded).
 * - cancelled: payout cancelled, candidate ineligible, or commission removed
 *              by an admin — excluded from totals.
 * - payable:   QUEUED for payout only. Under the commission lifecycle
 *              (PR-0b) a commission becomes payable only once an admin has
 *              committed it, the applicant's balance cleared, and it has
 *              been queued — earlier stages are never labelled "Payable".
 * - accruing:  everything else (pending / committed / not committed / under
 *              review) — the candidate is still progressing; NOT payable yet.
 */
export function commissionBucket(c: any): CommissionBucket {
  const status = String(c?.status || '').toLowerCase();
  if (c?.payoutProcessed || c?.payoutProcessedAt || PAID_STATUSES.has(status)) return 'paid';
  if (c?.payoutStatus === 'cancelled' || CANCELLED_STATUSES.has(status)) return 'cancelled';
  if (c?.payoutStatus === 'queued' || status === 'queued') return 'payable';
  return 'accruing';
}

export interface CommissionSummary {
  accruing: number;
  payable: number;
  paid: number;
  counts: Record<CommissionBucket, number>;
}

/** Totals by bucket — the single totals source for every referrer surface. */
export function summarizeCommissions(list: any[]): CommissionSummary {
  const out: CommissionSummary = {
    accruing: 0,
    payable: 0,
    paid: 0,
    counts: { accruing: 0, payable: 0, paid: 0, cancelled: 0 },
  };
  for (const c of list || []) {
    const bucket = commissionBucket(c);
    out.counts[bucket] += 1;
    if (bucket !== 'cancelled') out[bucket] += commissionAmount(c);
  }
  return out;
}

export interface CommissionHistoryEntry {
  label: string;
  detail?: string;
  at?: string;
}

function fmtAmount(n: any): string {
  const v = Number(n);
  return Number.isFinite(v) ? `$${v.toLocaleString()}` : '';
}

/**
 * Per-candidate commission history for the referrer-visible detail view:
 * the referral stamp plus any admin adjustments, attributed and dated.
 * Prefers an explicit `history[]` from the API (PR-0b); falls back to the
 * dated milestones the commissions merge already returns.
 */
export function commissionHistoryEntries(c: any): CommissionHistoryEntry[] {
  if (Array.isArray(c?.history) && c.history.length > 0) {
    return c.history.map((h: any) => {
      const amount =
        h.amount !== undefined
          ? fmtAmount(h.amount)
          : h.toAmount !== undefined
            ? `${fmtAmount(h.fromAmount)} → ${fmtAmount(h.toAmount)}`
            : '';
      const by = h.by || h.actor || h.adminEmail || h.createdBy || '';
      return {
        label: h.label || h.type || h.action || 'Commission update',
        detail: [amount, by ? `by ${by}` : '', h.note || h.reason || ''].filter(Boolean).join(' · ') || undefined,
        at: h.at || h.date || h.createdAt || undefined,
      };
    });
  }
  const entries: CommissionHistoryEntry[] = [];
  if (c?.createdAt) {
    entries.push({
      label: 'Referral recorded — commission stamped',
      detail: commissionAmount(c) ? `Stamped at ${fmtAmount(commissionAmount(c))}` : undefined,
      at: c.createdAt,
    });
  }
  if (c?.committedAt) entries.push({ label: 'Committed by ISATS', at: c.committedAt });
  if (c?.queuedAt) entries.push({ label: 'Queued for payout', at: c.queuedAt });
  if (c?.payoutProcessedAt) {
    entries.push({
      label: 'Payout processed',
      detail: c?.payment?.transactionId ? `Transaction ${c.payment.transactionId}` : undefined,
      at: c.payoutProcessedAt,
    });
  }
  if (c?.cancelledAt) {
    entries.push({ label: 'Commission cancelled', detail: c?.cancelReason || undefined, at: c.cancelledAt });
  }
  return entries;
}
